/**
 * §P · El marco del panel del club. **Con la marca del club.**
 *
 * Es la herramienta de quien lleva su club. Su color y su logo, porque quien la ve
 * por encima del hombro es el padre de un alumno.
 *
 * Esto era `club/App.tsx` y ahora es solo el marco: cabecera, menú, marca y la
 * comprobación de que hay club. **Las pantallas son rutas** (`shared/rutas`), y lo
 * que queda aquí es lo que no cambia al navegar.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import {
  membresiasActivas,
  useSesion,
  useYo,
} from '@/shared/auth/sesionContexto';
import {
  AvisoDelPago,
  AvisoSuspendido,
  Cargando,
  Vacio,
} from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { variablesDeMarca, type Marca } from '@/shared/ui/marca';
import { useImagen } from '@/shared/api/imagenes';
import { useAspecto } from '@/shared/ui/aspecto';
import { CambiarAspecto } from '@/shared/ui/CambiarAspecto';
import { BotonDelManual } from '@/shared/ui/BotonDelManual';
import { usePestanaActivaALaVista } from '@/shared/ui/usePestanaActivaALaVista';
import { useTextosPlegables } from '@/shared/ui/useTextosPlegables';
import { BarraInferior } from '@/shared/ui/BarraInferior';
import { Icono } from '@/shared/ui/Icono';
import { MenuDeCuenta } from '@/shared/ui/MenuDeCuenta';
import { Campana } from '@/shared/ui/pendientes/Campana';
import { FranjaDeUrgentes } from '@/shared/ui/pendientes/FranjaDeUrgentes';
import {
  aRol,
  puedeVerCuota,
  puedeVerLaMarca,
  puedeVerLoPendiente,
} from '@/club/permisos';
import { usePlanDelClub } from '@/club/plan';
import {
  seccionesDeBarra,
  seccionesPara,
  type Seccion,
} from '@/club/secciones';
import { ProveedorDelClub } from '@/club/contexto';
import { useHoy } from '@/club/hoy';
import { aPendientes } from '@/club/pendientes';

/**
 * Las secciones fijas de la barra de abajo del móvil, si el rol las tiene. Inicio va
 * siempre delante y no es una sección (no está en `SECCIONES`); el resto, en «Más».
 */
const EN_LA_BARRA: readonly Seccion[] = ['alumnos', 'calendario', 'cobros'];

function SelectorDeClub() {
  const yo = useYo();
  const { club, elegirClub } = useSesion();
  const activas = membresiasActivas(yo);

  // Con una sola membresía no se enseña selector (§5.2): un desplegable de un
  // elemento es ruido que además hace dudar de si falta algo.
  if (activas.length < 2) {
    return <span className="club-nombre">{activas[0]?.academy_name}</span>;
  }

  return (
    <label className="selector-club">
      <span className="sr">Club</span>
      <select value={club ?? ''} onChange={(e) => elegirClub(e.target.value)}>
        {activas.map((m) => (
          <option key={m.academy_id} value={m.academy_id}>
            {m.academy_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * El «Más» de la barra: lo que no se abre todos los días.
 *
 * Por qué existe y qué garantiza está en `club/secciones.ts`. Lo que se resuelve
 * aquí es que un desplegable hecho a mano se comporte: **`Escape` lo cierra y un
 * clic fuera también.** Sin las dos cosas, un menú que se queda abierto tapando la
 * pantalla es peor que no tener menú, y es el fallo típico de hacerlo sin librería.
 *
 * Y se cierra al navegar, porque si no se queda abierto encima de la pantalla nueva.
 */
function MenuDeMas({
  secciones,
  alElegir,
}: {
  /*
   * Lo de «Más» nunca incluye la sección abierta —`seccionesDeBarra` la sube a la
   * fila visible—, así que este botón no tiene estado activo. Tenía un `activa` que
   * siempre valía `false`: fuera, que un parámetro constante es una pregunta que
   * alguien se hará dentro de seis meses.
   */
  secciones: readonly { id: Seccion; etiqueta: string }[];
  alElegir: (id: Seccion) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;

    const alPulsarTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    const alClicarFuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };

    document.addEventListener('keydown', alPulsarTecla);
    document.addEventListener('mousedown', alClicarFuera);
    return () => {
      document.removeEventListener('keydown', alPulsarTecla);
      document.removeEventListener('mousedown', alClicarFuera);
    };
  }, [abierto]);

  return (
    <div className="mas" ref={caja}>
      <button
        type="button"
        className="btn-plano"
        aria-expanded={abierto}
        aria-haspopup="true"
        onClick={() => setAbierto((x) => !x)}
      >
        Más ▾
      </button>

      {abierto && (
        <ul className="mas-lista">
          {secciones.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="btn-plano"
                onClick={() => {
                  setAbierto(false);
                  alElegir(s.id);
                }}
              >
                {s.etiqueta}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MarcoDelPanel() {
  const yo = useYo();
  const { club, salir } = useSesion();
  usePestanaActivaALaVista();
  const lienzo = useRef<HTMLElement>(null);
  useTextosPlegables(lienzo);
  const activas = membresiasActivas(yo);
  const ir = useNavigate();
  /*
   * El camino actual, para saber qué entrada del menú va marcada. Se lee del
   * enrutador y no de un estado propio: dos fuentes para lo mismo es cómo el menú
   * acaba señalando una pantalla y enseñando otra.
   */
  const camino = useRouterState({ select: (s) => s.location.pathname });

  const membresia = activas.find((m) => m.academy_id === club) ?? activas[0];

  /*
   * El rol se calcula **antes** de la consulta de la marca, porque decide si se hace.
   * `aRol(undefined)` da `assistant`, el más limitado, así que mientras no haya
   * membresía tampoco se pide nada.
   */
  const rol = aRol(membresia?.role);

  const marca = useQuery({
    // El club entra en la clave: si no, cambiar de club reusaría la marca del
    // anterior y la cabecera mentiría durante un instante.
    queryKey: ['branding', club],
    /*
     * **A un entrenador no se le pide la marca.** `GET /branding` es `own` y `adm`,
     * así que le responde `403` en cada carga de página. Ver `puedeVerLaMarca`: sin
     * marca la interfaz sale con la paleta de la casa, que es lo correcto.
     */
    enabled: Boolean(club) && puedeVerLaMarca(rol),
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club: club! })
        .branding.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Marca;
    },
  });

  // El plan y la situación del pago (§A.2.1). Arriba, con la marca, porque de él
  // salen el aviso de la cabecera, el «solo lectura» y lo que el menú enseña.
  const plan = usePlanDelClub(membresia?.academy_id ?? null);

  // Lo pendiente (§P.13): la campana, la franja de lo urgente e Inicio. Solo para
  // quien abre el panel por derecho propio; el entrenador recibiría un 403.
  const hoy = useHoy(club ?? null, puedeVerLoPendiente(rol));
  const pendientes = hoy.data ? aPendientes(hoy.data.pending, rol) : undefined;

  const aspecto = useAspecto();

  /*
   * El logo en la cabecera y, mientras no hay —o mientras carga—, el cuadrado del
   * color, que es lo que había. En oscuro va el de fondo oscuro si el club lo
   * subió; si no, el claro sobre una placa blanca, que es donde se lee.
   */
  const logoOscuro = marca.data?.logo_dark_asset_id ?? null;
  const conPlaca = aspecto === 'oscuro' && !logoOscuro;
  const logo = useImagen(
    aspecto === 'oscuro' && logoOscuro ? logoOscuro : marca.data?.logo_asset_id
  );

  // Con el modo en las dependencias: van en línea en `<html>` y ganan a las de
  // `[data-tema='oscuro']`, así que hay que recalcularlas al cambiar de modo.
  const variables = useMemo(
    () => (marca.data ? variablesDeMarca(marca.data, aspecto) : {}),
    [marca.data, aspecto]
  );

  // Las variables se aplican en el elemento raíz y **solo aquí**: el back office
  // es neutro siempre.
  useEffect(() => {
    const raiz = document.documentElement;
    for (const [nombre, valor] of Object.entries(variables)) {
      raiz.style.setProperty(nombre, valor);
    }
    document.title = membresia ? `${membresia.academy_name} · Kiap` : 'Kiap';

    return () => {
      for (const nombre of Object.keys(variables)) {
        raiz.style.removeProperty(nombre);
      }
    };
  }, [variables, membresia]);

  if (!membresia) {
    return (
      <Vacio
        titulo="Tu cuenta no está en ningún club activo"
        explicacion="Si acabas de recibir una invitación, ábrela desde el correo. Si crees que es un error, habla con el dueño del club."
      />
    );
  }

  /*
   * «Solo lectura» tiene dos causas y un solo efecto en pantalla: el club
   * suspendido a mano por nosotros, o el pago vencido con la gracia agotada
   * (`lapsed`, §A.2.1). Las dos dejan leerlo todo y esconden los botones de guardar,
   * que el servidor rechazaría con un `423`.
   */
  const pago = plan.data?.payment;
  const suspendido =
    membresia.status === 'suspended' || pago?.standing === 'lapsed';
  /*
   * El rol se calculó arriba, junto a la consulta de la marca, y es el mismo: **un
   * rol, no un booleano.** §P.1 tiene cuatro reglas distintas para dos secciones de
   * la misma ficha —el entrenador escribe la ficha médica y no los contactos—, así
   * que un `puedeEscribir` no llega. Las reglas están en `club/permisos.ts`.
   */
  // Lo visible y lo de «Más». El camino entra porque la sección abierta se sube.
  const { visibles, enMas } = seccionesDeBarra(rol, camino);

  // La barra de abajo del móvil: tres secciones fijas —las que el rol tenga— y el
  // resto en «Más», con «Avisos» la primera.
  const suyas = seccionesPara(rol);
  const barra = suyas.filter((s) => EN_LA_BARRA.includes(s.id));
  const resto = suyas.filter((s) => !EN_LA_BARRA.includes(s.id));

  return (
    <div className="panel">
      {membresia.status === 'suspended' ? (
        <AvisoSuspendido contacto="hola@kiapclubs.com" />
      ) : pago ? (
        <AvisoDelPago
          situacion={pago.standing}
          pagadoHasta={fecha(pago.paid_through)}
          finDeLaGracia={fecha(pago.grace_ends_on)}
          diasQueQuedan={pago.days_left}
          vePago={puedeVerCuota(rol)}
          contacto="hola@kiapclubs.com"
        />
      ) : null}

      <header className="cabecera">
        <div className="cabecera-izq">
          {logo ? (
            <img
              className={`marca-logo${conPlaca ? ' marca-logo-placa' : ''}`}
              src={logo}
              alt=""
            />
          ) : (
            <span className="marca-punto" aria-hidden="true" />
          )}
          <SelectorDeClub />
        </div>
        <div className="cabecera-der">
          {puedeVerLoPendiente(rol) && (
            <Campana
              pendientes={pendientes}
              cargando={hoy.isPending}
              alIr={(destino) => void ir({ to: destino })}
            />
          )}
          {/* En el ordenador, a la vista; en el móvil, en el menú de cuenta. */}
          <div className="cabecera-escritorio">
            <CambiarAspecto />
            <BotonDelManual de="club" />
            {/*
              El correo lleva a la cuenta. Era texto muerto, y es donde todo el mundo
              busca «cambiar mi contraseña». 22-09-2026.
            */}
            <Link to="/mi-cuenta" className="quien" title={yo.user.email}>
              {yo.user.email}
            </Link>
            <button
              type="button"
              className="btn-plano"
              onClick={() => void salir()}
            >
              Salir
            </button>
          </div>
          <div className="cabecera-movil">
            <MenuDeCuenta
              nombre={yo.user.first_name}
              correo={yo.user.email}
              manual="club"
              alSalir={() => void salir()}
            />
          </div>
        </div>
      </header>

      {/* En Inicio, no: la lista está justo debajo y sería decirlo dos veces. */}
      {!camino.startsWith('/inicio') && (
        <FranjaDeUrgentes
          pendientes={pendientes}
          alIr={(destino) => void ir({ to: destino })}
          todas="/inicio"
        />
      )}

      <nav className="secciones" aria-label="Secciones del panel">
        <button
          type="button"
          className={
            camino.startsWith('/inicio') ? 'seccion-activa' : 'btn-plano'
          }
          onClick={() => void ir({ to: '/inicio' })}
        >
          <Icono nombre="inicio" />
          Inicio
        </button>
        {visibles.map((s) => (
          <button
            key={s.id}
            type="button"
            /*
             * `startsWith` y no igualdad: estando en `/alumnos/01a0…` la entrada
             * de «Alumnos» tiene que seguir marcada. Con igualdad, abrir una ficha
             * apaga el menú y nadie sabe dónde está.
             */
            className={
              camino.startsWith(`/${s.id}`) ? 'seccion-activa' : 'btn-plano'
            }
            onClick={() => void ir({ to: `/${s.id}` })}
          >
            <Icono nombre={s.id} />
            {s.etiqueta}
          </button>
        ))}

        {/* Sin nada dentro no se pinta: un menú vacío es una promesa incumplida. */}
        {enMas.length > 0 && (
          <MenuDeMas
            secciones={enMas}
            alElegir={(id) => void ir({ to: `/${id}` })}
          />
        )}
      </nav>

      <BarraInferior
        etiqueta="Secciones del panel"
        alIr={(destino) => void ir({ to: destino })}
        entradas={[
          {
            id: 'inicio',
            etiqueta: 'Inicio',
            icono: 'inicio',
            destino: '/inicio',
            activa: camino.startsWith('/inicio'),
            aviso: pendientes?.length || undefined,
          },
          ...barra.map((s) => ({
            id: s.id,
            etiqueta: s.etiqueta,
            icono: s.id,
            destino: `/${s.id}`,
            activa: camino.startsWith(`/${s.id}`),
          })),
        ]}
        mas={resto.map((s) => ({
          id: s.id,
          etiqueta: s.etiqueta,
          icono: s.id,
          destino: `/${s.id}`,
          activa: camino.startsWith(`/${s.id}`),
        }))}
      />

      <main className="lienzo" ref={lienzo}>
        {/*
          `isLoading` y no `isPending`: una consulta desactivada —la del entrenador—
          se queda en `pending` para siempre, así que con `isPending` el esqueleto no
          se iba nunca. `isLoading` es `pending` **y** buscando.
        */}
        {marca.isLoading && <Cargando filas={2} etiqueta="Cargando el club" />}

        {/*
          **Y si la marca falla, no se dice.** Es decoración: sin ella la interfaz
          sale con la paleta de la casa y todo funciona. Un aviso aquí sería un error
          permanente sobre algo que quien lo lee no puede arreglar, encima de todas
          las pantallas del panel —que es exactamente lo que le pasaba al entrenador
          antes de `puedeVerLaMarca`—.

          El club suspendido ya tenía su excepción por lo mismo, pero puesta un
          escalón más abajo: `AvisoSuspendido` lo cuenta arriba y bien, así que el
          `403` de la marca no añadía nada. Ahora la regla vale para los dos casos y
          para los que vengan.
        */}

        {club && (
          <ProveedorDelClub
            club={club}
            rol={rol}
            suspendido={suspendido}
            plan={plan.data}
          >
            <Outlet />
          </ProveedorDelClub>
        )}
      </main>
    </div>
  );
}
